import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Header, Footer } from "../common";
import MainContent from "../MainContent";
import KeyTariffFactsModal from "./KeyTariffFactsModal";
import { FilterTabs, FilterDropdown, QuotesList } from "./components";
import useQuotesData from "./hooks/useQuotesData";
import styles from "./QuotesScreen.module.css";

const QuotesScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedFilter, setSelectedFilter] = useState("12");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  // Get quotes data from location state (passed from previous pages)
  const apiQuotesData = location.state?.quotesData || [];
  const selectedFuel = location.state?.selectedFuel || "electricity";
  const formData = location.state?.formData || null;
  const singleRateBilling = formData?.singleRateBilling || false;
  const [initialLoad, setInitialLoad] = useState(false);
  const [isTabClicked, setIsTabClicked] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = "Join Engie! Quote";
  }, []);

  React.useEffect(() => {
    if (!apiQuotesData.length) {
      navigate("/", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { loading, error, monthOptions, filterQuotes } = useQuotesData(
    apiQuotesData,
    selectedFuel,
  );

  const handleFilterChange = (value) => {
    setSelectedFilter(value);
    setInitialLoad(true);
    if (selectedFilter === "monthly") {
      setIsTabClicked(false);
    }
  };

  const isDropdownVisible = () => {
    const fixedOptions = ["12", "24", "36", "48", "60"];
    const isFixedOption = fixedOptions.includes(selectedFilter);
    const isMonthlySelected = selectedFilter === "monthly";
    const shouldShowDropdown =
      isMonthlySelected || !initialLoad || !isTabClicked || !isFixedOption;

    return shouldShowDropdown;
  };

  const handleKeyTariffFacts = (quote) => {
    setSelectedQuote(quote);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedQuote(null);
  };

  const filteredQuotes = filterQuotes(selectedFilter);

  const renderQuotesContent = () => {
    if (loading) {
      return (
        <output className={styles.loading} aria-live="polite">
          Loading quotes...
        </output>
      );
    }
    if (error) {
      return (
        <div className={styles.error} role="alert" aria-live="assertive">
          Error: {error}
        </div>
      );
    }
    return (
      <QuotesList
        quotes={filteredQuotes}
        onKeyTariffFacts={handleKeyTariffFacts}
        formData={formData}
        singleRateBilling={singleRateBilling}
      />
    );
  };

  return (
    <div className={styles.mainContainer}>
      <Header height="220px" showShadow={false} />

      <MainContent>
        <div className={styles.quotesContainer}>
          <h1 id="quotes-heading" className={styles.pageTitle}>
            Your quotes
          </h1>

          {/* Contact Information */}
          <div className={styles.contactInfo}>
            <p>
              Speak to an Energy Advisor today to discuss your quote options.
            </p>
            <p>
              Call{" "}
              <a
                href="tel:03336660276"
                className={styles.phoneLink}
                aria-label="Call ENGIE at 0333 666 0276"
              >
                0333 666 0276
              </a>.
            </p>

            <p>
              Lines are open Mon - Thu 8.30am - 5.00pm and Fri 8.30am - 4.30pm.
            </p>
            <p className="text-xs text-center">
              View our Terms and Conditions{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.engie.co.uk/supply-information/contractual-information/"
                className="text-[#66afe9] hover:underline"
              >
                here
              </a>
            </p>
          </div>

          {/* Filter Tabs */}
          <FilterTabs
            selectedFilter={selectedFilter}
            onFilterChange={handleFilterChange}
            setInitialLoad={setInitialLoad}
            setIsTabClicked={setIsTabClicked}
          />

          {/* Filter Dropdown - Only show when MONTHLY is selected */}
          <FilterDropdown
            selectedFilter={selectedFilter}
            onFilterChange={handleFilterChange}
            monthOptions={monthOptions}
            isVisible={isDropdownVisible()}
            initialLoad={initialLoad}
            isTabClicked={isTabClicked}
          />

          {/* Quotes List */}
          {renderQuotesContent()}
          <div className={styles.disclaimerWrapper}>
            <p className={styles.disclaimer}>
              <small className={styles.disclaimerText}>
                *Your quote is valid until Sunday at 12 o'clock (midnight) only.
                Your quote is estimated based on the consumption information you
                provided. If your actual consumption is different, the final
                price may change.
              </small>
            </p>
          </div>
        </div>

        {/* Footer */}
        <Footer />
      </MainContent>

      {/* Key Tariff Facts Modal */}
      <KeyTariffFactsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        quote={selectedQuote}
        formData={formData}
      />
    </div>
  );
};

export default QuotesScreen;
